import { MODELS, type ModelUsage } from './models';

/**
 * Bloc de message (texte uniquement — APOLLO n'utilise pas l'image input).
 * Avec cache_control optionnel pour le prompt caching Anthropic.
 */
export interface TextBlock {
  type: 'text';
  text: string;
  cache_control?: { type: 'ephemeral' };
}

export type SystemPrompt = string | TextBlock[];

export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export interface CallClaudeOptions {
  usage: ModelUsage;
  /** Surcharge du modèle inféré depuis usage (rare) */
  model?: string;
  system?: SystemPrompt;
  messages: Message[];
  maxTokens?: number;
  temperature?: number;
  /** Délai d'attente max (ms) avant abandon */
  timeoutMs?: number;
  /** Tentatives en cas d'erreur réseau / 5xx (défaut 1 = pas de retry) */
  retries?: number;
}

export type AnthropicError =
  | { kind: 'network'; message: string }
  | { kind: 'timeout' }
  | { kind: 'http'; status: number; message: string }
  | { kind: 'parse'; message: string }
  | { kind: 'config'; message: string };

export type ClaudeResult =
  | { ok: true; text: string; raw: AnthropicResponse }
  | { ok: false; error: AnthropicError };

interface AnthropicResponseBlock { type: string; text?: string }
export interface AnthropicResponse {
  id?: string;
  content?: AnthropicResponseBlock[];
  stop_reason?: string;
  usage?: { input_tokens?: number; output_tokens?: number; cache_read_input_tokens?: number; cache_creation_input_tokens?: number };
  error?: { type: string; message: string };
}

const DEFAULT_TIMEOUT = 30_000;
const DEFAULT_MAX_TOKENS = 1500;

const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

/**
 * Appel unifié vers le proxy Cloudflare Worker (qui forwarde vers api.anthropic.com
 * en ajoutant x-api-key + anthropic-version côté serveur).
 *
 * Configurer VITE_API_BASE_URL (cf .env.example).
 */
export const callClaude = async (opts: CallClaudeOptions): Promise<ClaudeResult> => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    return { ok: false, error: { kind: 'config', message: 'VITE_API_BASE_URL non défini (voir .env.example)' } };
  }

  const model = opts.model ?? MODELS[opts.usage];
  const maxTokens = opts.maxTokens ?? DEFAULT_MAX_TOKENS;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT;
  const retries = Math.max(1, opts.retries ?? 1);

  const body = {
    model,
    max_tokens: maxTokens,
    ...(opts.temperature !== undefined ? { temperature: opts.temperature } : {}),
    ...(opts.system !== undefined ? { system: opts.system } : {}),
    messages: opts.messages,
  };

  let lastError: AnthropicError | null = null;
  for (let attempt = 1; attempt <= retries; attempt++) {
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), timeoutMs);

    try {
      const res = await fetch(`${baseUrl}/v1/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      clearTimeout(timeoutId);

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        const httpErr: AnthropicError = { kind: 'http', status: res.status, message: text || res.statusText };
        // Retry uniquement sur 5xx ou 429
        if ((res.status >= 500 || res.status === 429) && attempt < retries) {
          lastError = httpErr;
          await sleep(500 * attempt);
          continue;
        }
        return { ok: false, error: httpErr };
      }

      let data: AnthropicResponse;
      try {
        data = await res.json() as AnthropicResponse;
      } catch (err) {
        return { ok: false, error: { kind: 'parse', message: (err as Error).message } };
      }

      if (data.error) {
        return { ok: false, error: { kind: 'http', status: res.status, message: data.error.message } };
      }

      const blocks = data.content ?? [];
      const text = blocks
        .filter(b => b.type === 'text' && typeof b.text === 'string')
        .map(b => b.text!)
        .join('\n')
        .trim();

      return { ok: true, text, raw: data };
    } catch (err) {
      clearTimeout(timeoutId);
      const error = err as Error;
      if (error.name === 'AbortError') {
        lastError = { kind: 'timeout' };
      } else {
        lastError = { kind: 'network', message: error.message };
      }
      if (attempt < retries) {
        await sleep(500 * attempt);
        continue;
      }
    }
  }

  return { ok: false, error: lastError ?? { kind: 'network', message: 'unknown' } };
};

/** Message d'erreur lisible pour l'utilisateur */
export const formatError = (err: AnthropicError): string => {
  switch (err.kind) {
    case 'timeout': return 'Délai dépassé. Réessaie ?';
    case 'network': return 'Pas de connexion. Vérifie ton réseau.';
    case 'http':
      if (err.status === 401 || err.status === 403) return 'Authentification refusée par le proxy.';
      if (err.status === 429) return 'Trop de requêtes, attends quelques secondes.';
      if (err.status >= 500) return 'Serveur Anthropic indisponible. Réessaie plus tard.';
      return `Erreur ${err.status} : ${err.message}`;
    case 'parse': return 'Réponse incorrecte du proxy.';
    case 'config': return 'Application mal configurée. Contacte ton dev (toi).';
  }
};
